import { Suspense, lazy } from 'react';
import { Route, Routes } from 'react-router-dom';

import { IModule, Permissions, ReportTypes } from '@cloud-wave/neon-common-lib';
import { LEGACY_PAGE_ROUTES, PAGE_ROUTES } from '@cloud-wave/neon-common-lib/app';

import { useContactContext } from 'lib/common/contexts/ContactContext';
import IncomingTaskOverlay from 'lib/common/contexts/ContactContext/components/IncomingTaskOverlay';
import { useModulesContext } from 'lib/common/contexts/ModulesContext';

import { useHistory } from 'lib/common/hooks/useHistory';

// components
import AuthorisedRoute from 'lib/common/components/AuthorisedRoute';
import Loader from 'lib/common/components/Loader';
import PermissionRedirect from 'lib/common/components/PermissionRedirect';
import WithApiContexts from 'lib/common/components/atoms/WithApiContexts';

import isDevEnvironment from 'lib/common/constants/isDevEnvironment';
// constants
import { LAYOUT_SWITCH_REDIRECTION_ROUTES } from 'lib/routes/constants';

import { CoreLayout } from 'lib/common/layouts';

import { adminOnly, supervisorOrAdmin } from '../userRoleCombinations';

const AgentWorkspace = lazy(() => import('lib/pages/AgentWorkspace'));
const CreateCustomerProfile = lazy(() => import('lib/pages/CreateCustomerProfile'));
const CustomerProfile = lazy(() => import('lib/pages/CustomerProfile'));
const CustomerProfiles = lazy(() => import('lib/pages/CustomerProfiles'));
const EmailView = lazy(() => import('lib/pages/EmailView'));
const InteractionHistoryPage = lazy(() => import('lib/pages/InteractionHistory'));
const Playground = lazy(() => import('lib/pages/Playground'));
const Users = lazy(() => import('lib/pages/Users'));
const Report = lazy(() => import('lib/pages/desktop/Report'));
const PageModule = lazy(() => import('lib/common/components/PageModule'));
const NotFound = lazy(() => import('lib/common/components/NotFound'));

export default function DesktopRoutes({ user }) {
  const {
    state: { pageModules },
  } = useModulesContext();
  const {
    state: { tasks },
  } = useContactContext();

  const { Navigate } = useHistory();

  return (
    <CoreLayout user={user}>
      <IncomingTaskOverlay tasks={tasks} />
      <Suspense fallback={<Loader centered />}>
        <Routes>
          <Route path="/" element={<Navigate to={PAGE_ROUTES.WORKSPACE} />} />
          <Route path="/home" element={<Navigate to={PAGE_ROUTES.WORKSPACE} />} />
          <Route path={LEGACY_PAGE_ROUTES.DASHBOARDS} element={<Navigate to={PAGE_ROUTES.WORKSPACE} />} />
          <Route path={LEGACY_PAGE_ROUTES.WALLBOARD} element={<Navigate to={PAGE_ROUTES.WORKSPACE} />} />
          <Route path={PAGE_ROUTES.BASE} element={<Navigate to={PAGE_ROUTES.WORKSPACE} />} />
          <Route path={PAGE_ROUTES.SETTINGS} element={<Navigate to={PAGE_ROUTES.USER_SETTINGS} />} />
          <Route element={<AgentWorkspace user={user} />} path={PAGE_ROUTES.WORKSPACE} />
          <Route path={PAGE_ROUTES.EMAIL} element={<EmailView />} />
          {isDevEnvironment && <Route path={PAGE_ROUTES.PLAYGROUND} element={<Playground />} />}
          <Route
            path={PAGE_ROUTES.REPORTS}
            element={
              <PermissionRedirect
                allowedRoles={supervisorOrAdmin}
                redirects={[
                  {
                    permission: Permissions.REPORTS_PERFORMANCE_TODAY,
                    route: PAGE_ROUTES.REPORTS_PERFORMANCE_TODAY,
                  },
                  {
                    permission: Permissions.REPORTS_ANALYTICS,
                    route: PAGE_ROUTES.REPORTS_ANALYTICS,
                  },
                ]}
              />
            }
          />
          <Route
            path={PAGE_ROUTES.REPORTS_PERFORMANCE_TODAY}
            element={
              <AuthorisedRoute
                permissionType="tenant"
                permission={Permissions.REPORTS_PERFORMANCE_TODAY}
                allowedRoles={supervisorOrAdmin}
              >
                <WithApiContexts
                  component={Report}
                  props={{ type: ReportTypes.PERFORMANCE_TODAY }}
                  user={user}
                  key={ReportTypes.PERFORMANCE_TODAY}
                />
              </AuthorisedRoute>
            }
          />
          <Route
            path={PAGE_ROUTES.REPORTS_ANALYTICS}
            element={
              <AuthorisedRoute
                permissionType="tenant"
                permission={Permissions.REPORTS_ANALYTICS}
                allowedRoles={supervisorOrAdmin}
              >
                <WithApiContexts
                  component={Report}
                  props={{ type: ReportTypes.ANALYTICS }}
                  user={user}
                  key={ReportTypes.ANALYTICS}
                />
              </AuthorisedRoute>
            }
          />
          <Route
            path={PAGE_ROUTES.REPORTS_WALLBOARD}
            element={
              <AuthorisedRoute permissionType="tenant" permission={Permissions.REPORTS_WALLBOARD}>
                <WithApiContexts
                  component={Report}
                  props={{ type: ReportTypes.WALLBOARD }}
                  user={user}
                  key={ReportTypes.WALLBOARD}
                />
              </AuthorisedRoute>
            }
          />

          <Route
            path={PAGE_ROUTES.INTERACTION_HISTORY}
            element={
              <AuthorisedRoute permission={Permissions.CALL_HISTORY} permissionType="tenant">
                <InteractionHistoryPage />
              </AuthorisedRoute>
            }
          />
          <Route
            path={PAGE_ROUTES.CUSTOMER_PROFILES}
            element={
              <AuthorisedRoute permissionType="tenant" permission={Permissions.CUSTOMER_PROFILES}>
                <CustomerProfiles />
              </AuthorisedRoute>
            }
          />
          <Route
            path={PAGE_ROUTES.CREATE_CUSTOMER_PROFILE}
            element={
              <AuthorisedRoute permission={Permissions.CUSTOMER_PROFILES_WRITE}>
                <CreateCustomerProfile />
              </AuthorisedRoute>
            }
          />
          <Route
            path={PAGE_ROUTES.CUSTOMER_PROFILE}
            element={
              <AuthorisedRoute permissionType="tenant" permission={Permissions.CUSTOMER_PROFILES}>
                <CustomerProfile />
              </AuthorisedRoute>
            }
          />

          <Route
            element={
              <AuthorisedRoute allowedRoles={adminOnly}>
                <WithApiContexts component={Users} user={user} />
              </AuthorisedRoute>
            }
            path={`${PAGE_ROUTES.USER_SETTINGS}/*`}
          />

          {pageModules.map((pageModule: IModule, i) => (
            <Route
              key={i}
              path={pageModule.pathName}
              element={<PageModule pageModuleProps={pageModule} user={user} />}
            />
          ))}
          {LAYOUT_SWITCH_REDIRECTION_ROUTES.map((props, index) => (
            <Route key={index} path={props.from} element={<Navigate to={props.to} />} />
          ))}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
    </CoreLayout>
  );
}
